@if (isMobile() && mobileShowCallDetail()) {
  <div class="shrink-0 mb-3 space-y-2">
    <button
      type="button"
      (click)="backToCallList()"
      class="flex items-center gap-1.5 text-sm font-bold text-slate-500 dark:text-slate-400 hover:text-[#A446FF] transition-colors cursor-pointer"
    >
      <app-icon-v2 name="ArrowLeft" [size]="16" />
      All calls
    </button>
    <div class="rounded-xl bg-slate-100 dark:bg-slate-900/60 p-1 grid grid-cols-2 gap-1">
      <button
        type="button"
        (click)="selectMobileDetailTab('transcript')"
        class="px-2 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer"
        [ngClass]="mobileDetailTab() === 'transcript'
          ? 'bg-white dark:bg-slate-800 text-[#A446FF] shadow-sm'
          : 'text-slate-500 dark:text-slate-400'"
      >
        Transcript
      </button>
      <button
        type="button"
        (click)="selectMobileDetailTab('context')"
        class="px-2 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer"
        [ngClass]="mobileDetailTab() === 'context'
          ? 'bg-white dark:bg-slate-800 text-[#A446FF] shadow-sm'
          : 'text-slate-500 dark:text-slate-400'"
      >
        Context
      </button>
    </div>
  </div>
}

<div
  class="min-h-[70vh] max-h-[73vh] items-stretch gap-4 xl:gap-5"
  [ngClass]="isMobile() ? 'flex flex-col' : 'grid grid-cols-[2fr_6fr_4fr]'"
>
  <!-- LEFT: Ongoing Lines (2fr) -->
  <div
    class="relative min-w-0 flex flex-col min-h-[280px] min-h-[70vh] max-h-[73vh] md:max-h-[73vh] bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 overflow-hidden"
    [ngClass]="{ 'flex-1': isMobile() }"
    [class.hidden]="isMobile() && mobileShowCallDetail()"
  >
    <div class="shrink-0 flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
      <div class="flex items-center gap-2">
        <span class="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
        <h2 class="text-xs font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase">Ongoing Lines</h2>
      </div>
      <div class="flex items-center gap-2">
        <button
          type="button"
          (click)="refreshOngoingCalls()"
          [disabled]="isListLoading() || isListRefreshing()"
          class="p-1.5 rounded-lg text-slate-400 hover:text-[#A446FF] hover:bg-purple-50 dark:hover:bg-white/10 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          title="Refresh ongoing calls"
        >
          <app-icon-v2
            name="refresh-cw"
            [size]="14"
            [customClass]="isListRefreshing() ? 'animate-spin text-[#A446FF]' : ''"
          />
        </button>
        <span class="text-xs font-bold text-slate-400 tabular-nums">{{ ongoingCalls().length }}</span>
      </div>
    </div>

    @if (isListLoading()) {
      <div class="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 py-12">
        <div class="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
        <p class="text-sm font-medium text-slate-500">Loading ongoing calls...</p>
      </div>
    } @else if (listError()) {
      <div class="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 py-8 px-4 text-center">
        <app-icon-v2 name="AlertCircle" [size]="32" customClass="text-red-400" />
        <p class="text-sm text-slate-500">{{ listError() }}</p>
        <button
          type="button"
          (click)="retryList()"
          class="px-4 py-2 rounded-xl bg-[#A446FF] text-white text-sm font-bold hover:bg-[#9235FF] transition-colors cursor-pointer"
        >
          Retry
        </button>
      </div>
    } @else if (ongoingCalls().length === 0) {
      <div class="flex-1 min-h-0" aria-hidden="true"></div>
      <div class="absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-2 px-4 text-center">
        <app-icon-v2 name="headset" [size]="36" customClass="text-slate-300" />
        <p class="text-sm font-bold text-slate-700 dark:text-slate-300">No active calls</p>
        <p class="text-xs text-slate-500">Ongoing calls will appear here in real time.</p>
      </div>
    } @else {
      <div class="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
        @for (call of ongoingCalls(); track callKey(call)) {
          <button
            type="button"
            (click)="selectCallFromList(call)"
            class="w-full text-left rounded-2xl border p-3 transition-all cursor-pointer"
            [ngClass]="selectedCallKey() === callKey(call)
              ? 'border-[#A446FF] bg-purple-50/80 dark:bg-purple-900/20 ring-1 ring-purple-500/20 shadow-sm'
              : 'border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-purple-200 dark:hover:border-line-strong'"
          >
            <div class="flex items-start justify-between gap-2 mb-1.5">
              <h3 class="text-sm font-bold text-[#141437] dark:text-white truncate flex-1" [title]="call.businessName">
                {{ call.businessName }}
              </h3>
              <span class="text-xs font-bold text-slate-500 shrink-0 tabular-nums">
                {{ callDurations().get(callKey(call)) }}
              </span>
            </div>
            @if (call.phoneNumber) {
              <p class="text-xs text-slate-500 font-medium mb-2">{{ call.phoneNumber }}</p>
            }
            <div class="flex items-center justify-between gap-2 pt-2 border-t border-slate-50 dark:border-slate-700/50">
              <span class="text-xs font-bold text-slate-600 dark:text-slate-300 truncate">{{ call.agentName }}</span>
              <span class="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold border" [ngClass]="sentimentClass(call.sentiment)">
                {{ call.sentiment }}
              </span>
            </div>
          </button>
        }
      </div>
    }
  </div>

  <!-- CENTER: Transcription (6fr) -->
  <div
    class="relative min-w-0 flex flex-col min-h-[360px] min-h-[70vh] max-h-[73vh] md:max-h-[73vh] bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 overflow-hidden"
    [ngClass]="{ 'flex-1': isMobile() }"
    [class.hidden]="!isMobileTranscriptVisible()"
  >
    @if (!selectedCall()) {
      <div class="flex-1 min-h-0" aria-hidden="true"></div>
      <div class="absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-2 px-4 text-center">
        <app-icon-v2 name="headset" [size]="36" customClass="text-slate-300" />
        <p class="text-sm font-bold text-slate-700 dark:text-slate-300">Select an ongoing call</p>
        <p class="text-xs text-slate-500">Choose a call from the list to view live transcription.</p>
      </div>
    } @else {
      @let detail = callDetail();
      @let call = selectedCall()!;

      <div class="shrink-0 p-4 border-b border-slate-100 dark:border-slate-700">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2 mb-1">
              <h2 class="text-lg font-bold text-[#141437] dark:text-white truncate">{{ detail?.businessName || call.businessName }}</h2>
              <span class="px-2 py-0.5 rounded-full text-[10px] font-bold border" [ngClass]="sentimentClass(detail?.sentiment || call.sentiment)">
                {{ detail?.sentiment || call.sentiment }}
              </span>
            </div>
            <div class="flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
              <span class="flex items-center gap-1">
                <app-icon-v2 name="user" [size]="12" />
                {{ detail?.agentName || call.agentName }}
              </span>
              @if (detail?.phoneNumber || call.phoneNumber) {
                <span>{{ detail?.phoneNumber || call.phoneNumber }}</span>
              }
              @if (detail?.platform || call.platform) {
                <span>{{ detail?.platform || call.platform }}</span>
              }
            </div>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <span class="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
            <span class="text-sm font-bold text-slate-700 dark:text-slate-200 tabular-nums">{{ selectedDuration() }}</span>
          </div>
        </div>
      </div>

      <div class="flex-1 min-h-0 overflow-hidden">
        @if (isDetailLoading() && !detail) {
          <div class="h-full flex flex-col items-center justify-center gap-3">
            <div class="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            <p class="text-sm text-slate-500">Loading call details...</p>
          </div>
        } @else if (detailError() && !detail) {
          <div class="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <app-icon-v2 name="AlertCircle" [size]="32" customClass="text-red-400" />
            <p class="text-sm text-slate-500">{{ detailError() }}</p>
            <button
              type="button"
              (click)="retryDetail()"
              class="px-4 py-2 rounded-xl bg-[#A446FF] text-white text-sm font-bold cursor-pointer"
            >
              Retry
            </button>
          </div>
        } @else if (detail) {
          <app-call-transcription
            class="h-full block"
            [messages]="detail.transcript"
            [isChatView]="true"
            [autoScrollToBottom]="true"
            [productName]="detail.productName || call.productName"
            [meetingUrl]="detail.platform || call.platform || 'Live Call'"
            [dateTime]="'Live'"
            [agents]="detail.agentName"
          ></app-call-transcription>
        }
      </div>
    }
  </div>

  <!-- RIGHT: Cheatsheet / Sales Pitch / Summary (4fr) -->
  <div
    class="relative min-w-0 flex flex-col min-h-[280px] min-h-[70vh] max-h-[73vh] md:max-h-[73vh] bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 overflow-hidden"
    [ngClass]="{ 'flex-1': isMobile() }"
    [class.hidden]="!isMobileContextVisible()"
  >
    @if (!selectedCall() || !callDetail()) {
      <div class="flex-1 min-h-0" aria-hidden="true"></div>
      <div class="absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-2 px-4 text-center">
        <app-icon-v2 name="bookOpen" [size]="36" customClass="text-slate-300" />
        <p class="text-sm font-bold text-slate-700 dark:text-slate-300">Context panel</p>
        <p class="text-xs text-slate-500">Select a call to view cheatsheet, sales pitch, and summary.</p>
      </div>
    } @else {
      @let detail = callDetail()!;

      <!-- Segmented tabs -->
      <div class="shrink-0 p-2 m-2 rounded-xl bg-slate-100 dark:bg-slate-900/60 grid grid-cols-3 gap-1">
        <button
          type="button"
          (click)="selectContextTab('cheatsheet')"
          class="px-2 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer"
          [ngClass]="activeContextTab() === 'cheatsheet'
            ? 'bg-white dark:bg-slate-800 text-[#A446FF] shadow-sm'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'"
        >
          Cheatsheet
        </button>
        <button
          type="button"
          (click)="selectContextTab('salesPitch')"
          class="px-2 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer"
          [ngClass]="activeContextTab() === 'salesPitch'
            ? 'bg-white dark:bg-slate-800 text-[#A446FF] shadow-sm'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'"
        >
          Sales Pitch
        </button>
        <button
          type="button"
          (click)="selectContextTab('summary')"
          class="px-2 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer"
          [ngClass]="activeContextTab() === 'summary'
            ? 'bg-white dark:bg-slate-800 text-[#A446FF] shadow-sm'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'"
        >
          Summary
        </button>
      </div>

      <div class="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
        @if (activeContextTab() === 'cheatsheet') {
          @if (detail.cheatSheet.length === 0 && detail.objectionHandlers.length === 0) {
            <div class="py-8 text-center">
              <p class="text-sm text-slate-500">No cheat sheet available for this call yet.</p>
            </div>
          } @else {
            @if (detail.cheatSheet.length) {
              <div class="space-y-2 mb-4">
                @for (faq of detail.cheatSheet; track faq.title; let fi = $index) {
                  <div class="rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
                    <button
                      type="button"
                      (click)="toggleFaq(fi)"
                      class="w-full flex items-center gap-2.5 px-3 py-3 text-left bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors cursor-pointer"
                    >
                      <span class="w-6 h-6 shrink-0 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center text-[#A446FF] text-xs font-bold">?</span>
                      <span class="flex-1 text-sm font-semibold text-slate-800 dark:text-slate-200">{{ faq.title }}</span>
                      <app-icon-v2
                        [name]="expandedFaqIndex() === fi ? 'chevronUp' : 'chevronDown'"
                        [size]="16"
                        customClass="text-slate-400 shrink-0"
                      />
                    </button>
                    @if (expandedFaqIndex() === fi) {
                      <div class="px-3 pb-3 pt-0 border-t border-slate-50 dark:border-slate-700/50">
                        <markdown class="insight-markdown text-sm text-slate-600 dark:text-slate-400 leading-relaxed" [data]="faq.content"></markdown>
                      </div>
                    }
                  </div>
                }
              </div>
            }

            @if (detail.objectionHandlers.length) {
              <div class="pt-2 border-t border-slate-100 dark:border-slate-700">
                <div class="flex items-center gap-2 mb-3 px-1">
                  <app-icon-v2 name="shield" [size]="14" customClass="text-amber-600" />
                  <p class="text-[10px] font-bold uppercase tracking-wider text-slate-500">Objection Handlers</p>
                </div>
                <div class="space-y-2">
                  @for (handler of detail.objectionHandlers; track handler.objection) {
                    <div class="p-3 rounded-xl bg-amber-50/80 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/30">
                      @if (handler.objection) {
                        <p class="text-sm font-bold text-amber-900 dark:text-amber-200 mb-1">"{{ handler.objection }}"</p>
                      }
                      @if (handler.response) {
                        <p class="text-xs text-amber-800/90 dark:text-amber-100/80 leading-relaxed">{{ handler.response }}</p>
                      }
                    </div>
                  }
                </div>
              </div>
            }
          }
        }

        @if (activeContextTab() === 'salesPitch') {
          @if (detail.salesPitch.length === 0) {
            <div class="py-8 text-center">
              <p class="text-sm text-slate-500">No sales pitch available for this call yet.</p>
            </div>
          } @else {
            <div class="space-y-3">
              @for (section of detail.salesPitch; track section.title) {
                <div class="p-4 rounded-xl bg-white dark:bg-slate-900 border border-violet-100 dark:border-violet-900/40 shadow-sm">
                  <p class="text-sm font-bold text-violet-700 dark:text-violet-300 mb-2">{{ section.title }}</p>
                  <markdown class="insight-markdown text-sm text-slate-700 dark:text-slate-300 leading-relaxed" [data]="section.content"></markdown>
                </div>
              }
            </div>
          }
        }

        @if (activeContextTab() === 'summary') {
          @if (!detail.summaryLogView) {
            <div class="py-8 text-center">
              <p class="text-sm text-slate-500">No summary available for this call yet.</p>
            </div>
          } @else {
            @let summary = detail.summaryLogView;
            <div class="space-y-3">
              @if (summary.managementSummary) {
                <div class="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700">
                  <p class="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Management Summary</p>
                  <p class="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{{ summary.managementSummary }}</p>
                </div>
              }
              @if (summary.highlights.length) {
                <div class="p-4 rounded-xl bg-emerald-50/70 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800/30">
                  <p class="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-2 flex items-center gap-1">
                    <app-icon-v2 name="TrendingUp" [size]="12" /> Interest Signals
                  </p>
                  <ul class="space-y-1.5">
                    @for (h of summary.highlights; track $index) {
                      <li class="text-sm text-emerald-900 dark:text-emerald-100/90 flex items-start gap-2">
                        <span class="text-emerald-500 mt-0.5">•</span>
                        <span>{{ h }}</span>
                      </li>
                    }
                  </ul>
                </div>
              }
              @if (summary.callFlow.length) {
                <div class="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700">
                  <p class="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Call Flow</p>
                  <div class="space-y-2">
                    @for (step of summary.callFlow; track $index) {
                      <div>
                        @if (step.title) {
                          <p class="text-sm font-semibold text-slate-800 dark:text-slate-200">{{ step.title }}</p>
                        }
                        @if (step.summary) {
                          <p class="text-sm text-slate-600 dark:text-slate-400 mt-0.5">{{ step.summary }}</p>
                        }
                      </div>
                    }
                  </div>
                </div>
              }
              @if (summary.majorIssues.length) {
                <div class="p-4 rounded-xl bg-red-50/70 dark:bg-red-900/10 border border-red-100 dark:border-red-800/30">
                  <p class="text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400 mb-2">Open Queries &amp; Concerns</p>
                  <ul class="space-y-1.5">
                    @for (issue of summary.majorIssues; track $index) {
                      <li class="text-sm text-red-900 dark:text-red-100/90 flex items-start gap-2">
                        <span class="text-red-500 mt-0.5">•</span>
                        <span>{{ issue }}</span>
                      </li>
                    }
                  </ul>
                </div>
              }
            </div>
          }
        }
      </div>
    }
  </div>
</div>
